import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { nonNullable } from 'lib/array/arrayUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { ManualItineraryDeleteRequest, TripItineraryData } from 'tripPlanner/api/types'
import { isLeFlightSavedItemData } from 'tripPlanner/api/types'
import { isMultiCartHotelItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import {
  computeCartPanelTotal,
  isCartPanelItemCheckoutReady,
  shouldIncludeCartPanelItemPriceEstimate,
  shouldShowReadyItemsOnlyNote,
} from '../utils/cartPanelUtils'
import { isFlightSearchExpired } from '../utils/flightCartPanelUtils'
import { useCartPanelItems, type CartPanelAccommodationItem, type CartPanelFlightItem, type CartPanelItem } from './useCartPanelItems'
import { useCartPanelActions } from './useCartPanelActions'

export type { CartPanelItem }

interface UseCartPanelArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  chatId: number | undefined
  isStreaming: boolean
  sendMessage: (message: string) => Promise<void>
  deleteManualItineraryTarget: (target: ManualItineraryDeleteRequest) => Promise<void>
  isLoggedIn: boolean
}

interface UseCartPanelReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  isError: boolean
  totalPrice: number
  totalValue: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  loyaltyCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest>
  isLoggedIn: boolean
  handleToggleInclude: (item: CartPanelItem) => Promise<void>
  handleSetDates: () => void
  handleChooseTickets: (item: CartPanelItem) => void
  handleChooseDate: (item: CartPanelItem) => void
  handleChooseTime: (item: CartPanelItem) => void
  handleChooseDifferentTickets: (item: CartPanelItem) => void
  handleChooseFlight: (item: CartPanelFlightItem) => Promise<void>
  handleRefreshFlight: (item: CartPanelFlightItem) => Promise<void>
  handleChangeFlight: (item: CartPanelFlightItem) => void
  handleViewFlightDetails: (item: CartPanelFlightItem) => void
  refreshingFlightKey: string | undefined
  handleDelete: (item: CartPanelItem) => void
  handleCheckout: () => Promise<void>
  isCheckingOut: boolean
  refetch: () => void
  flightSearchDrawer: ReactNode
}

function getAccommodationLoyaltyCalculationRequest(
  item: CartPanelAccommodationItem,
  offers: Record<string, App.AnyOffer>,
  currentRegionCode: string,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (!item.offerId || !item.pricing.price) return undefined

  const rawOffer = offers[item.offerId]
  const offer = rawOffer && isLEHotel(rawOffer) ? rawOffer : undefined
  if (!offer) return undefined

  const cartHotelItem = item.cartItem && isMultiCartHotelItem(item.cartItem) ? item.cartItem : undefined
  const packageId = cartHotelItem?.packageId ?? item.packageId
  const roomRateId = cartHotelItem?.roomRateId ?? item.roomRateId
  const duration = item.duration ?? cartHotelItem?.duration
  if (!packageId || !roomRateId || !duration) return undefined

  const packageUniqueKey = getPackageUniqueKey(packageId, duration, roomRateId)
  const pkg = offer.packages.find(candidate => candidate.uniqueKey === packageUniqueKey)
  if (!pkg) return undefined

  return generateLoyaltyPointsCalculatorHotelOptions(offer, {
    pkg,
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice,
    currentRegionCode,
    nights: duration,
  })
}

function getItemLoyaltyCalculationRequest(
  item: CartPanelItem,
  offers: Record<string, App.AnyOffer>,
  currentRegionCode: string,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (item.cartExcluded || !shouldIncludeCartPanelItemPriceEstimate(item)) return undefined

  switch (item.productType) {
    case 'accommodation':
      return getAccommodationLoyaltyCalculationRequest(item, offers, currentRegionCode)
    case 'experience':
      if (!item.pricing.price) return undefined
      return {
        price: item.pricing.price,
        memberPrice: item.pricing.memberPrice ?? 0,
        offerId: item.experienceId,
        loyaltyProductType: 'experience',
      }
    case 'flight':
      return undefined
  }
}

export function useCartPanel({
  multiCartId,
  itineraryData,
  chatId,
  isStreaming,
  sendMessage,
  deleteManualItineraryTarget,
  isLoggedIn,
}: UseCartPanelArgs): UseCartPanelReturn {
  const [optimisticExclusions, setOptimisticExclusions] = useState<Record<number, boolean>>({})
  const [refreshFailedFlightKeys, setRefreshFailedFlightKeys] = useState<Record<string, boolean>>({})
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const offers = useAppSelector(getAllOffers)
  const { currentRegionCode } = useContext(GeoContext)

  const { items, isLoading, isError, refetch, refetchCartItems } = useCartPanelItems({
    multiCartId,
    itineraryData,
    isLoggedIn,
    optimisticExclusions,
    refreshFailedFlightKeys,
  })

  const actions = useCartPanelActions({
    chatId,
    multiCartId,
    items,
    isStreaming,
    isLoggedIn,
    sendMessage,
    deleteManualItineraryTarget,
    setOptimisticExclusions,
    setRefreshFailedFlightKeys,
    refetchCartItems,
  })

  // Multi-city legs carry a journeyKey but no fare to choose, so they should
  // never sit in `needs_flight_selection` waiting on a manual "Select fares"
  // click (the tile hides that banner for multi-city). Auto-run the modal-free,
  // search-free conversion for each leg, one at a time. Each key is attempted a
  // bounded number of times so a transient backend failure can recover, but a
  // persistently failing leg can't spin forever.
  const AUTO_CONVERT_MAX_ATTEMPTS = 3
  const autoConvertAttemptsRef = useRef<Map<string, number>>(new Map())
  const autoConvertInFlightRef = useRef(false)
  const { handleChooseFlight, isFlightCartMutationPending } = actions

  useEffect(() => {
    // Skip while any flight cart mutation is in flight (e.g. a manual refresh):
    // handleChooseFlight would no-op anyway, but firing here would still burn one
    // of the bounded retry attempts on a leg that never actually converted.
    if (isStreaming || !isLoggedIn || autoConvertInFlightRef.current || isFlightCartMutationPending) return

    const leg = items.find((item): item is CartPanelFlightItem =>
      item.productType === 'flight' &&
      item.cartStatus === 'needs_flight_selection' &&
      !item.cartExcluded &&
      isLeFlightSavedItemData(item.savedItemData) &&
      item.savedItemData.flightGroupType === 'multi_city' &&
      !!item.savedItemData.journeyKey &&
      !isFlightSearchExpired(item.savedItemData.searchExpiry) &&
      (autoConvertAttemptsRef.current.get(item.key) ?? 0) < AUTO_CONVERT_MAX_ATTEMPTS,
    )
    if (!leg) return

    autoConvertAttemptsRef.current.set(leg.key, (autoConvertAttemptsRef.current.get(leg.key) ?? 0) + 1)
    autoConvertInFlightRef.current = true
    // Conversion can throw (e.g. the fallback live search rejects on a network
    // error); swallow it so the bounded retry above governs recovery instead of
    // an unhandled rejection. The flag still resets so the next leg is tried.
    Promise.resolve(handleChooseFlight(leg)).catch(() => {}).finally(() => {
      autoConvertInFlightRef.current = false
    })
  }, [items, isStreaming, isLoggedIn, handleChooseFlight, isFlightCartMutationPending])

  const { totalPrice, totalValue, totalItemCount } = useMemo(
    () => computeCartPanelTotal(
      items.map(i => ({
        cartExcluded: i.cartExcluded,
        price: i.pricing.price,
        memberPrice: i.pricing.memberPrice,
        value: i.pricing.value,
        includeInTotal: shouldIncludeCartPanelItemPriceEstimate(i),
        includeInCount: true,
      })),
      hasLuxPlusBenefits,
    ),
    [items, hasLuxPlusBenefits],
  )

  const hasCheckoutReadyItems = useMemo(
    () => items.some(isCartPanelItemCheckoutReady),
    [items],
  )

  const showReadyItemsOnlyNote = useMemo(
    () => shouldShowReadyItemsOnlyNote(items),
    [items],
  )
  const loyaltyCalculationRequests = useMemo(
    () => nonNullable(items.map(item => getItemLoyaltyCalculationRequest(item, offers, currentRegionCode))),
    [items, offers, currentRegionCode],
  )

  return {
    items,
    isLoading,
    isError,
    totalPrice,
    totalValue,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    loyaltyCalculationRequests,
    isLoggedIn,
    ...actions,
    refetch,
  }
}
